import { useForm } from "react-hook-form";
import { Button, Card, Hairline, Input, Select } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { parseSpendingLimitInput } from "../lib/limit-validation.js";
import type {
  CustomerDetailDTO,
  CustomerRole,
  CustomerTier,
} from "@wmart/shared-types";
import {
  CustomerRoleValues,
  CustomerTierValues,
  formatTierWithDiscount,
} from "@wmart/shared-types";

interface EditFormValues {
  tier: CustomerTier;
  role: CustomerRole;
  default_location_id: string;
  spending_limit_minor: string; // kept as string until parse-on-submit
}

interface Props {
  customer: CustomerDetailDTO;
  onSuccess: () => void | Promise<void>;
}

/**
 * Edit form for an approved customer. Submits PATCH /v1/admin/customers/:id
 * with all three fields. UpdateCustomerBody refines to require at least one
 * field present, but submitting all three is always safe — the API
 * "no-change skip" optimisation will silently noop the PATCH (no audit row)
 * when the values match.
 *
 * The default-location dropdown is sourced from `customer.allowed_locations`
 * (the customer's M:N grants) rather than the parent company's full location
 * set: the default ship-to must be one of the customer's allowed locations
 * (the same invariant the customer setup flow enforces).
 */
export function CustomerEditForm({ customer, onSuccess }: Props) {
  const { notify, notifyProblem } = useToast();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<EditFormValues>({
    defaultValues: {
      tier: customer.tier,
      role: customer.role,
      // `customer.default_location_id` may be null on the wire if it has been
      // cleared out of band; coerce to "" so the controlled select works.
      default_location_id: customer.default_location_id ?? "",
      spending_limit_minor:
        customer.spending_limit_minor === null
          ? ""
          : String(customer.spending_limit_minor),
    },
  });

  async function onSubmit(values: EditFormValues) {
    const parsedLimit = parseSpendingLimitInput(values.spending_limit_minor);
    if (parsedLimit === "invalid") {
      notify("error", "Spending limit must be empty or an integer ≥ 0");
      return;
    }

    // Only include in PATCH body if changed from initial.
    const initialLimit = customer.spending_limit_minor;
    const limitChanged = parsedLimit !== initialLimit;

    const body: Record<string, unknown> = {
      tier: values.tier,
      role: values.role,
      default_location_id: values.default_location_id,
    };
    if (limitChanged) {
      body.spending_limit_minor = parsedLimit;
    }

    try {
      await api.patch(`/v1/admin/customers/${customer.id}`, body);
      notify("success", "Customer updated");
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Save failed");
    }
  }

  const allowedLocations = customer.allowed_locations;

  return (
    <Card as="section" className="p-6">
      <h2 className="font-serif text-title-lg text-ink-900">Edit</h2>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-4 flex flex-col gap-4"
      >
        <div className="flex gap-3">
          <div className="flex-1">
            <Select label="Tier" {...register("tier", { required: true })}>
              {CustomerTierValues.map((t) => (
                <option key={t} value={t}>
                  {formatTierWithDiscount(t)}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex-1">
            <Select label="Role" {...register("role", { required: true })}>
              {CustomerRoleValues.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <Select
          label="Default location"
          disabled={allowedLocations.length === 0}
          {...register("default_location_id", { required: true })}
        >
          <option value="">
            {allowedLocations.length === 0
              ? "No allowed locations — assign one below first"
              : "Select a default location…"}
          </option>
          {allowedLocations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} — {l.address.city}, {l.address.postcode}
            </option>
          ))}
        </Select>

        <Input
          type="text"
          inputMode="numeric"
          label="Per-order spending limit (minor units; £1 = 100)"
          helper="Empty = no limit. 0 = block every order."
          {...register("spending_limit_minor")}
        />

        <Hairline />

        <div>
          <Button
            type="submit"
            disabled={isSubmitting || allowedLocations.length === 0}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </form>
    </Card>
  );
}
